import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { useRecordPayrollAddOn } from '../../lib/payroll/use-payroll.js';
import { formatCurrency } from '../../lib/format.js';
import { safeRupees } from './safe-rupees.js';

/**
 * A bonus, an allowance or a reimbursement, on top of what the cycle earned.
 *
 * THE REASON IS REQUIRED and it is checked here before anything is sent, against the same
 * minimum the shared schema enforces. "₹2,000 extra" with nothing beside it is not an
 * explanation, and a staff member who cannot read the working for a figure is exactly the
 * argument this module exists to prevent — so an empty reason is refused inline, on the screen,
 * rather than being sent and bounced back as a server error.
 */
/**
 * Mirrors `payrollAddOnReasonSchema`'s own `.min(3)` in the shared package, so the screen
 * refuses exactly what the server would refuse rather than sending a request to find out.
 */
const REASON_MIN_LENGTH = 3;

export interface PayrollAddOnDialogProps {
  staffUserId: string;
  staffName: string;
  onClose: () => void;
}

export function PayrollAddOnDialog({
  staffUserId,
  staffName,
  onClose,
}: PayrollAddOnDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const record = useRecordPayrollAddOn(staffUserId);

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const amountPaise = safeRupees(amount);
    const trimmedReason = reason.trim();
    if (trimmedReason.length < REASON_MIN_LENGTH) {
      setError(t('payroll.reasonRequired'));
      return;
    }
    if (amountPaise <= 0) {
      setError(t('payroll.amountRequired'));
      return;
    }
    setError(null);
    try {
      await record.mutateAsync({
        amountPaise,
        reason: trimmedReason,
        ...(occurredAt ? { occurredAt } : {}),
      });
      toast({
        title: t('payroll.addOnRecorded', {
          amount: formatCurrency(amountPaise),
          name: staffName,
        }),
        variant: 'success',
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('payroll.addOnDialogTitle')}</DialogTitle>
          <DialogDescription>{t('payroll.addOnDialogHint')}</DialogDescription>
        </DialogHeader>

        {error ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm text-muted-foreground">{t('payroll.staffMember')}</span>
            <span className="font-medium">{staffName}</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-add-on-amount">{t('payroll.amount')}</Label>
            <Input
              id="payroll-add-on-amount"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-add-on-reason">{t('payroll.reason')}</Label>
            <Input
              id="payroll-add-on-reason"
              value={reason}
              placeholder={t('payroll.reasonPlaceholder')}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-add-on-date">{t('payroll.addOnDate')}</Label>
            <Input
              id="payroll-add-on-date"
              type="date"
              value={occurredAt}
              onChange={(event) => setOccurredAt(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={record.isPending} onClick={() => void submit()}>
            {t('payroll.addOnSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
